/**
 * Shared contract constants and validators for the AI Crew Builder.
 *
 * This file is the executable half of CONTRACTS.md. Import it; never retype
 * a string literal from the contract into your own module.
 *
 * Backend:  const C = require('./contracts');           (copy lives in services/crewCall/)
 * Frontend: import * as C from './contracts';           (copy lives in src/vyn/modules/Crew/crewCall/)
 *
 * Kept dependency-free and dual-export so the same file works in CommonJS and ESM.
 * Owner: Kaustubh. Changes go through DECISIONS.md first.
 */

const ANALYZE_STATUS = Object.freeze({
  READY: 'READY',
  NEEDS_INPUT: 'NEEDS_INPUT',
  NO_MATCH: 'NO_MATCH',
  ANSWER: 'ANSWER', // read-only question answered at analyze time; nothing to confirm
});

const EXECUTE_STATUS = Object.freeze({
  SUCCESS: 'SUCCESS',
  PARTIAL: 'PARTIAL',
  CONFLICT: 'CONFLICT',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
});

const TIER = Object.freeze({
  RECOMMENDED: 'RECOMMENDED',
  ELIGIBLE: 'ELIGIBLE',
});

const SOURCE = Object.freeze({
  PROMPT: 'PROMPT',
  TEMPLATE: 'TEMPLATE',
  PRECEDENT: 'PRECEDENT',
  PROFILE: 'PROFILE',
  NONE: 'NONE',
});

const CHIP_KIND = Object.freeze({ PLUS: 'plus', CAUTION: 'caution' });

const CHIP_CODE = Object.freeze({
  EVENT_CONTINUITY: 'EVENT_CONTINUITY',
  TASK_EXP: 'TASK_EXP',
  VENUE_EXP: 'VENUE_EXP',
  LEAD_AFFINITY: 'LEAD_AFFINITY',
  SHIFT_FIT: 'SHIFT_FIT',
  RECENT_TOGETHER: 'RECENT_TOGETHER',
  SHORT_REST: 'SHORT_REST',
  WEEK_LOAD: 'WEEK_LOAD',
  NEW_TO_TASK: 'NEW_TO_TASK',
  DST_LONG_SHIFT: 'DST_LONG_SHIFT',
  MULTI_CREW_DAY: 'MULTI_CREW_DAY',
  ATTENDANCE: 'ATTENDANCE',
});

const CLARIFY_FIELD = Object.freeze({
  eventId: 'eventId',
  taskCode: 'taskCode',
  date: 'date',
  start: 'start',
  end: 'end',
  headcount: 'headcount',
  crewLeadId: 'crewLeadId',
  crewRef: 'crewRef',
  employeeRef: 'employeeRef',
  keepShift: 'keepShift',
  newOwnerId: 'newOwnerId',
  targetDate: 'targetDate',
  venue: 'venue',
});

/** Which pipeline an analyze/execute call runs through. CREATE_CREW is the original (and
 * default, when a caller omits the field) flow -- TRANSFER_OWNER/COPY_CREW skip labor-
 * context resolution and worker-candidate ranking entirely, since neither builds a new
 * crew from scratch. */
const INTENT_TYPE = Object.freeze({
  CREATE_CREW: 'CREATE_CREW',
  TRANSFER_OWNER: 'TRANSFER_OWNER',
  COPY_CREW: 'COPY_CREW',
  EMPLOYEE_SCHEDULE: 'EMPLOYEE_SCHEDULE', // read-only: an employee's shifts over a date range
  CREW_MEMBERS: 'CREW_MEMBERS',           // read-only: who is on a crew
  ADD_TO_CREW: 'ADD_TO_CREW',
  REMOVE_FROM_CREW: 'REMOVE_FROM_CREW',
  SET_CREW_LEAD: 'SET_CREW_LEAD',
});

const POLICY = Object.freeze({
  HISTORY_FIRST: 'HISTORY_FIRST',   // default
  BALANCED: 'BALANCED',             // rotation weighted up
});

/** Stored proposal doc's execution.state (section 7). Brain-owned per D-17 -- the Engine's
 * executeProposal does not read or write this; it just returns a result. */
const EXECUTION_STATE = Object.freeze({
  PENDING: 'PENDING',
  CREATING: 'CREATING',
  SYNCING: 'SYNCING',
  DONE: 'DONE',
  FAILED: 'FAILED',
});

const ERROR_CODE = Object.freeze({
  LLM_UNAVAILABLE: 'LLM_UNAVAILABLE',
  NOT_AUTHORIZED: 'NOT_AUTHORIZED',
  INVALID_REQUEST: 'INVALID_REQUEST',
  INVALID_SELECTION: 'INVALID_SELECTION',
  PROPOSAL_NOT_FOUND: 'PROPOSAL_NOT_FOUND',
  UKG_REAUTH_REQUIRED: 'UKG_REAUTH_REQUIRED',
  UKG_AUTH_UNAVAILABLE: 'UKG_AUTH_UNAVAILABLE',
  INTERNAL: 'INTERNAL',
});

const LIMITS = Object.freeze({
  MAX_PROMPT_CHARS: 500,
  PROPOSAL_TTL_MINUTES: 30,
  MAX_HEADCOUNT: 100,
  LLM_TIMEOUT_MS: 4000,
  ANALYZE_TARGET_P50_MS: 4000,
  EVENT_LOOKAHEAD_DAYS: 7,
  EVENT_LOOKBACK_DAYS: 1,
  HISTORY_WINDOW_DAYS: 90,
  PRECEDENT_WINDOW_DAYS: 30,
  PRECEDENT_MIN_CREWS: 3,
  PRECEDENT_MIN_SHARE: 0.7,
  MAX_PLUS_CHIPS: 3,
  MAX_CAUTION_CHIPS: 2,
});

const API = Object.freeze({
  BASE: '/api/crew-call',
  ANALYZE: '/api/crew-call/analyze',
  CLARIFY: '/api/crew-call/clarify',
  EXECUTE: '/api/crew-call/execute',
  CONFIG: '/api/crew-call/config',
});

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Shape check for an analyze/clarify response. Returns an array of problem strings;
 * empty array means it matches the contract.
 *
 * Use it in tests on both sides. The UI should also run it against live responses in
 * development so drift is caught the moment it appears, not on integration day.
 */
function validateProposalResponse(res) {
  const errs = [];
  const push = (m) => errs.push(m);

  if (!res || typeof res !== 'object') return ['response is not an object'];
  if (!Object.values(ANALYZE_STATUS).includes(res.status)) push(`bad status: ${res.status}`);
  if (typeof res.proposalId !== 'string') push('proposalId missing');
  if (typeof res.traceId !== 'string') push('traceId missing');
  if (!Array.isArray(res.clarifications)) push('clarifications must be an array');

  if (res.status === ANALYZE_STATUS.READY) {
    const c = res.crew || {};
    if (!YMD.test(c.date || '')) push('crew.date must be YYYY-MM-DD');
    if (!HHMM.test(c.start || '')) push('crew.start must be HH:mm');
    if (!HHMM.test(c.end || '')) push('crew.end must be HH:mm');
    if (typeof c.headcount !== 'number') push('crew.headcount must be a number');
    if (typeof c.crossesMidnight !== 'boolean') push('crew.crossesMidnight must be boolean');
    if (!c.event || !c.event.code) push('crew.event.code missing');
    if (!c.task || !c.task.code) push('crew.task.code missing');

    if (!res.sources || typeof res.sources !== 'object') push('sources missing');
    else {
      for (const [k, v] of Object.entries(res.sources)) {
        if (!v || !Object.values(SOURCE).includes(v.type)) push(`sources.${k}.type invalid`);
      }
    }

    if (!Array.isArray(res.candidates)) push('candidates must be an array');
    else {
      res.candidates.forEach((cand, i) => {
        if (typeof cand.employeeId !== 'string') push(`candidates[${i}].employeeId must be a string`);
        if (typeof cand.name !== 'string') push(`candidates[${i}].name missing`);
        if (typeof cand.rank !== 'number') push(`candidates[${i}].rank missing`);
        if (!Object.values(TIER).includes(cand.tier)) push(`candidates[${i}].tier invalid`);
        if (typeof cand.preselected !== 'boolean') push(`candidates[${i}].preselected missing`);
        (cand.chips || []).forEach((chip, j) => {
          if (!Object.values(CHIP_CODE).includes(chip.code)) push(`candidates[${i}].chips[${j}].code invalid`);
          if (!Object.values(CHIP_KIND).includes(chip.kind)) push(`candidates[${i}].chips[${j}].kind invalid`);
          if (!chip.text) push(`candidates[${i}].chips[${j}].text missing`);
        });
      });
      const ranks = res.candidates.map((c2) => c2.rank);
      if (ranks.some((r, i) => i > 0 && r <= ranks[i - 1])) push('candidates not sorted by rank ascending');
    }

  }

  if (res.status === ANALYZE_STATUS.NEEDS_INPUT && (res.clarifications || []).length === 0) {
    push('NEEDS_INPUT with no clarifications');
  }
  res.clarifications?.forEach((cl, i) => {
    if (!Object.values(CLARIFY_FIELD).includes(cl.field)) push(`clarifications[${i}].field invalid`);
    if (!cl.question) push(`clarifications[${i}].question missing`);
    if (!Array.isArray(cl.options)) push(`clarifications[${i}].options must be an array`);
  });

  return errs;
}

/**
 * Shape check for the Engine's buildCandidates() return value (section 6): the exact
 * contract shape, nothing more -- not the full downstream /analyze envelope (that's
 * validateProposalResponse, which runs on what the Brain assembles afterward, not on the
 * Engine's raw output). Returns an array of problem strings; empty means it matches.
 */
function validateBuildCandidatesResult(res) {
  const errs = [];
  const push = (m) => errs.push(m);

  if (!res || typeof res !== 'object') return ['engine result is not an object'];

  if (!Array.isArray(res.candidates)) push('candidates must be an array');
  else {
    res.candidates.forEach((cand, i) => {
      if (!cand || typeof cand !== 'object') { push(`candidates[${i}] is not an object`); return; }
      if (typeof cand.employeeId !== 'string') push(`candidates[${i}].employeeId must be a string`);
      if (typeof cand.rank !== 'number') push(`candidates[${i}].rank must be a number`);
      if (!Object.values(TIER).includes(cand.tier)) push(`candidates[${i}].tier invalid`);
    });
  }

  if (!res.counts || typeof res.counts !== 'object') push('counts missing');
  if (!res.policy || typeof res.policy !== 'object') push('policy missing');

  return errs;
}

/** Shape check for an /execute response. */
function validateExecuteResponse(res) {
  const errs = [];
  if (!res || typeof res !== 'object') return ['response is not an object'];
  if (!Object.values(EXECUTE_STATUS).includes(res.status)) errs.push(`bad status: ${res.status}`);
  if (typeof res.requested !== 'number') errs.push('requested missing');
  if (typeof res.scheduled !== 'number') errs.push('scheduled missing');
  for (const k of ['failed', 'conflicts', 'replacements']) {
    if (!Array.isArray(res[k])) errs.push(`${k} must be an array`);
  }
  if (res.scheduledWorkers !== undefined && !Array.isArray(res.scheduledWorkers)) {
    errs.push('scheduledWorkers must be an array when present');
  }
  if (res.status === EXECUTE_STATUS.SUCCESS && res.scheduled !== res.requested) {
    errs.push('SUCCESS must have scheduled === requested, use PARTIAL otherwise');
  }
  if (res.status === EXECUTE_STATUS.CONFLICT && res.crewId) {
    errs.push('CONFLICT must not create a crew');
  }
  return errs;
}

/**
 * Shape check for a TRANSFER_OWNER analyze/clarify response -- the same common envelope
 * validateProposalResponse checks (status/proposalId/traceId/clarifications), plus its own
 * minimal READY payload. TRANSFER_OWNER has no crew/candidates/sources shape (no new crew
 * is built, no worker candidates are ranked), so this does not reuse
 * validateProposalResponse's body checks.
 */
function validateTransferOwnerResponse(res) {
  const errs = [];
  const push = (m) => errs.push(m);

  if (!res || typeof res !== 'object') return ['response is not an object'];
  if (!Object.values(ANALYZE_STATUS).includes(res.status)) push(`bad status: ${res.status}`);
  if (typeof res.proposalId !== 'string') push('proposalId missing');
  if (typeof res.traceId !== 'string') push('traceId missing');
  if (!Array.isArray(res.clarifications)) push('clarifications must be an array');

  if (res.status === ANALYZE_STATUS.READY) {
    if (typeof res.crewId !== 'string') push('crewId missing');
    if (typeof res.newOwnerId !== 'string') push('newOwnerId missing');
  }
  if (res.status === ANALYZE_STATUS.NEEDS_INPUT && (res.clarifications || []).length === 0) {
    push('NEEDS_INPUT with no clarifications');
  }
  res.clarifications?.forEach((cl, i) => {
    if (!Object.values(CLARIFY_FIELD).includes(cl.field)) push(`clarifications[${i}].field invalid`);
    if (!cl.question) push(`clarifications[${i}].question missing`);
    if (!Array.isArray(cl.options)) push(`clarifications[${i}].options must be an array`);
  });

  return errs;
}

/**
 * Shape check for a COPY_CREW analyze/clarify response -- same envelope convention as
 * validateTransferOwnerResponse, with its own minimal READY payload.
 */
function validateCopyCrewResponse(res) {
  const errs = [];
  const push = (m) => errs.push(m);

  if (!res || typeof res !== 'object') return ['response is not an object'];
  if (!Object.values(ANALYZE_STATUS).includes(res.status)) push(`bad status: ${res.status}`);
  if (typeof res.proposalId !== 'string') push('proposalId missing');
  if (typeof res.traceId !== 'string') push('traceId missing');
  if (!Array.isArray(res.clarifications)) push('clarifications must be an array');

  if (res.status === ANALYZE_STATUS.READY) {
    if (typeof res.crewId !== 'string') push('crewId missing');
    if (!YMD.test(res.targetDate || '')) push('targetDate must be YYYY-MM-DD');
  }
  if (res.status === ANALYZE_STATUS.NEEDS_INPUT && (res.clarifications || []).length === 0) {
    push('NEEDS_INPUT with no clarifications');
  }
  res.clarifications?.forEach((cl, i) => {
    if (!Object.values(CLARIFY_FIELD).includes(cl.field)) push(`clarifications[${i}].field invalid`);
    if (!cl.question) push(`clarifications[${i}].question missing`);
    if (!Array.isArray(cl.options)) push(`clarifications[${i}].options must be an array`);
  });

  return errs;
}

const contracts = {
  ANALYZE_STATUS, EXECUTE_STATUS, TIER, SOURCE, EXECUTION_STATE, INTENT_TYPE,
  CHIP_KIND, CHIP_CODE, CLARIFY_FIELD, POLICY, ERROR_CODE,
  LIMITS, API, HHMM, YMD,
  validateProposalResponse, validateExecuteResponse, validateBuildCandidatesResult,
  validateTransferOwnerResponse, validateCopyCrewResponse,
};

if (typeof module !== 'undefined' && module.exports) module.exports = contracts;
if (typeof window !== 'undefined') window.__AI_CREW_CONTRACTS__ = contracts;
